<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      One in five Australians lives with chronic pain. In addition to this number, every day many
      people experience acute pain, occurring post-surgery or from accidents such as burns or
      fractures.
    </p>
    <div class="vx-row">
      <div class="vx-col lg:w-1/2">
        <p class="mb-5">
          Effective pain assessments are crucial for patient care. Not only does controlled pain
          improve the patient’s comfort, but it also improves other areas of their health, including
          their psychological and physical function. This is why it is important for all health
          professionals to be able to complete an accurate pain assessment and implement successful
          pain management strategies.
        </p>
        <p class="mb-5">
          A pain scale is a tool that practitioners use to help assess a person’s pain. A person
          usually self-reports their pain using a specially designed scale, sometimes with the help
          of a doctor, parent, or guardian. Pain scales may be used during the start of a
          consultation to better understand certain aspects of a person’s pain. Some of these
          aspects are pain duration, severity, and type.
        </p>
      </div>
      <div class="vx-col lg:w-1/2">
        <centered-image-wrapper
          :image="require('@/assets/images/experiences/physiotherapy/chronic_pain.png')"
          alt=""
          classes="3/3"
        />
      </div>
    </div>

    <p class="mb-5">
      Pain scales can also help to make an accurate diagnosis, create a treatment plan, and measure
      the effectiveness of treatment. Pain scales exist for people of all ages, from newborns to
      seniors, as well as people with impaired communication skills.
    </p>
    <h4 class="mb-5">Unidimensional Pain Scales</h4>
    <p class="mb-5">
      These pain scales are a simple way for people to rate the intensity of their pain. They use
      words, images, or descriptors to measure pain or pain relief.
    </p>
    <p class="mb-5">Some common unidimensional pain scales include:</p>
    <ul class="default-list mb-5">
      <li>Numeric Rating Scale (NRS)</li>
      <li>Visual Analog Scale (VAS)</li>
      <li>The Comparative Pain Scale</li>
    </ul>

    <h4 class="mb-5">Numeric Rating Scale</h4>
    <centered-image-wrapper
      :image="require('@/assets/images/experiences/physiotherapy/numeric_pain.png')"
      alt="0 to 10 numeric pain rating scale: a horizontal line marked 0 to 10. 0 is labelled None, 1 to 3 Mild, 4 to 6 Moderate and 7 to 10 Severe."
      classes="3/3"
    />
    <p class="mb-5">
      This is the most commonly used pain scale. A person rates their pain on a scale of 0 to 10 or
      0 to 5. Zero means “no pain,” and 5 or 10 means “the worst possible pain.”
    </p>
    <p class="mb-5">
      These pain intensity levels may be assessed upon initial treatment, or periodically after
      treatment.
    </p>

    <h4 class="mb-5">Visual Analog Scale (VAS)</h4>
    <centered-image-wrapper
      :image="require('@/assets/images/experiences/physiotherapy/visual_analog.png')"
      alt="Visual analog scale: a horizontal line labelled No pain at the left end and Worst possible pain at the right end, with a cross marked about a third of the way along."
      classes="3/3"
    />
    <p class="mb-5">
      This pain scale shows a 10-centimetre line printed on a piece of paper, with anchors at either
      end. At one end is “no pain,” and at the other end are “pain as bad as it could be” or “the
      worst imaginable pain.”
    </p>
    <p class="mb-5">
      The person marks a spot or X on the line to show their pain intensity. A doctor then measures
      the line with a ruler to come up with a pain score.
    </p>

    <h4 class="mb-5">Comparative Pain Scale</h4>
    <p class="mb-5">
      This pain scale contains several different elements to help assess pain intensity, including
      pictures of facial expressions, a numerical scale, and various verbal descriptions of pain
      levels.
    </p>
    <p class="mb-5">
      A person uses all of the information displayed in the chart to self-rate by choosing the
      number that best corresponds to how much pain they feel.
    </p>
    <centered-image-wrapper
      :image="require('@/assets/images/experiences/physiotherapy/pain_scale.jpg')"
      alt="Comparative pain scale chart, 0 to 10, with a face for each number. 0 Pain free: No pain, feeling perfectly normal. 1 Very mild, 2 Discomforting, 3 Tolerable: Minor pain - nagging, annoying, but doesn't interfere with most daily living activities; patient able to adapt to pain psychologically and with medication or devices such as cushions. 4 Distressing, 5 Very distressing, 6 Intense: Moderate pain - interferes significantly with daily living activities; requires lifestyle changes but patient remains independent; patient unable to adapt pain. 7 Very intense, 8 Utterly horrible, 9 Excruciating unbearable, 10 Unimaginable unspeakable: Severe pain - disabling; unable to perform daily living activities; unable to engage in normal activities; patient is disabled and unable to function independently."
      classes="3/3"
    />

    <p class="mb-5">
      You are now going to complete 2 pain assessment tasks. Before attempting these you
      <strong><u>MUST</u></strong> watch the video below.
    </p>
    <button
      type="button"
      class="no-style img-click-button"
      aria-label="Play video: Pain scales"
      @click="showVideoPopup(videos[1].video)"
    >
      <img :src="videos[1].image" class="img-responsive mx-auto cursor-pointer mb-5" alt="" />
    </button>

    <h4 class="mb-5">Task 1: My Pain Self-Assessment</h4>
    <p class="mb-5">
      Now, you can practice using the scales to assess the levels of pain you may (or may not) be
      feeling.
    </p>

    <div class="overflow-hidden mb-5">
      <p class="float-left py-2 pr-2">Type of pain (headache, injury etc.)</p>
      <text-block v-model="data.question1_1" :editable="isEditable" class="w-full" />
    </div>

    <h4 class="mb-5">0 - 10 Numeric Pain Rating Scale</h4>
    <p class="mb-5">
      Move the slider so that it corresponds to the level of pain you are feeling (as you slide,
      there will be a green number that displays to give you the pain level number).
    </p>

    <div class="vx-row mb-5">
      <div class="vx-row m-auto lg:w-2/3 xs:w-full">
        <div class="vx-col xs:w-2/12 text-right">
          <span class="">None</span>
        </div>
        <div class="vx-col xs:w-8/12">
          <!--                                             border-left: 3px rgba(var(&#45;&#45;vs-primary),1) solid;-->
          <!--                                             border-right: 3px rgba(var(&#45;&#45;vs-primary),1) solid;">-->

          <accessible-slider
            v-model="data.question1_2"
            label="Pain intensity now, 0 to 10"
            :disabled="!isEditable"
            min-text="None"
            max-text="Worst possible pain"
          />
          <div class="vx-row">
            <div class="vx-col xs:w-2/12">
              <img
                src="@/assets/images/experiences/physiotherapy/slider-none.png"
                class="img-responsive"
                alt="Bracket marking the start of the scale, labelled None."
              />
            </div>
            <div class="vx-col xs:w-3/12">
              <img
                src="@/assets/images/experiences/physiotherapy/slider-mild.png"
                class="img-responsive"
                alt="Bracket marking the next section of the scale, labelled Mild."
              />
            </div>
            <div class="vx-col xs:w-3/12">
              <img
                src="@/assets/images/experiences/physiotherapy/slider-moderate.png"
                class="img-responsive"
                alt="Bracket marking the middle section of the scale, labelled Moderate."
              />
            </div>
            <div class="vx-col xs:w-4/12">
              <img
                src="@/assets/images/experiences/physiotherapy/slider-severe.png"
                class="img-responsive"
                alt="Bracket marking the upper section of the scale, labelled Severe."
              />
            </div>
          </div>
        </div>
        <div class="vx-col xs:w-2/12 text-left">
          <span>Severe</span>
        </div>
      </div>
    </div>

    <h4 class="mb-5">Visual Analog Scale (VAS)</h4>
    <p class="mb-5">
      Move the slider along the bar to the appropriate place to show your level of pain between "No
      pain" and "Worst possible pain"
    </p>
    <div class="vx-row mb-5">
      <div class="vx-row m-auto lg:w-2/3 xs:w-full">
        <div class="vx-col xs:w-2/12 text-right" style="align-self: center">
          <span>No pain</span>
        </div>
        <div
          class="vx-col xs:w-8/12"
          style="
            align-self: center;
            border-left: 3px rgba(var(--vs-primary), 1) solid;
            border-right: 3px rgba(var(--vs-primary), 1) solid;
          "
        >
          <accessible-slider
            v-model="data.question2"
            :max="100"
            label="Visual analogue pain scale, 0 to 100"
            :disabled="!isEditable"
            min-text="No pain"
            max-text="Worst possible pain"
          />
        </div>
        <div class="vx-col xs:w-2/12 text-left" style="align-self: center">
          <span>Worst possible pain</span>
        </div>
      </div>
    </div>
    <!--    <centered-image-wrapper :image="require('@/assets/images/experiences/physiotherapy/no_pain_worst_pain.png')" classes="3/3"/>-->

    <h4 class="mb-5">Comparative Pain Scale</h4>
    <p class="mb-5">
      Look at the Comparative Pain Scale Chart below. Choose the number that best corresponds to how
      much pain you are feeling and record in the space below.
    </p>
    <centered-image-wrapper
      :image="require('@/assets/images/experiences/physiotherapy/comparative_pain_scale.png')"
      alt="Comparative pain scale chart, 0 to 10, with a face for each number. 0 Pain free: No pain, feeling perfectly normal. 1 Very mild, 2 Discomforting, 3 Tolerable: Minor pain - nagging, annoying, but doesn't interfere with most daily living activities; patient able to adapt to pain psychologically and with medication or devices such as cushions. 4 Distressing, 5 Very distressing, 6 Intense: Moderate pain - interferes significantly with daily living activities; requires lifestyle changes but patient remains independent; patient unable to adapt pain. 7 Very intense, 8 Utterly horrible, 9 Excruciating unbearable, 10 Unimaginable unspeakable: Severe pain - disabling; unable to perform daily living activities; unable to engage in normal activities; patient is disabled and unable to function independently."
      classes="3/3"
    />
    <div class="overflow-hidden mb-5">
      <h5 class="float-left py-2 pr-2">Comparative Pain Scale Result:</h5>
      <text-block
        v-model="data.question3_1"
        :editable="isEditable"
        label="Comparative Pain Scale Result"
        style="width: 350px"
      />
    </div>

    <h4 class="mb-5">Task 2: Family Member / Friend’s Self-Assessment</h4>
    <p class="mb-5">
      Now, can you find a ‘guinea-pig’ to use the scales? This could be a friend or a family member.
      Explain how each scale works and have them rate the level of pain they are currently feeling
      accordingly.
    </p>
    <div class="overflow-hidden mb-5">
      <span class="float-left py-2 pr-2">Person's Name: </span>
      <text-block
        v-model="data.question3_1"
        :editable="isEditable"
        class="xs:w-full sm:w-full md:w-2/3 lg:w-2/3"
      />
    </div>
    <div class="overflow-hidden mb-5">
      <span class="float-left py-2 pr-2">Type of Pain: </span>
      <text-block
        v-model="data.question3_3"
        :editable="isEditable"
        class="xs:w-full sm:w-full md:w-2/3 lg:w-2/3"
      />
    </div>

    <h4 class="mb-5">0 - 10 Numeric Pain Rating Scale</h4>
    <p class="mb-5">
      Move the slider so that it corresponds to the level of pain you are feeling (as you slide,
      there will be a green number that displays to give you the pain level number).
    </p>
    <!--    <centered-image-wrapper :image="require('@/assets/images/experiences/physiotherapy/numeric_pain.png')" classes="3/3"/>-->
    <div class="vx-row mb-5">
      <div class="vx-row m-auto lg:w-2/3 xs:w-full">
        <div class="vx-col xs:w-2/12 text-right">
          <span class="">None</span>
        </div>
        <div class="vx-col xs:w-8/12">
          <!--                                             border-left: 3px rgba(var(&#45;&#45;vs-primary),1) solid;-->
          <!--                                             border-right: 3px rgba(var(&#45;&#45;vs-primary),1) solid;">-->

          <accessible-slider
            v-model="data.question4"
            label="Pain intensity at its worst, 0 to 10"
            :disabled="!isEditable"
            min-text="None"
            max-text="Worst possible pain"
          />
          <div class="vx-row">
            <div class="vx-col xs:w-2/12">
              <img
                src="@/assets/images/experiences/physiotherapy/slider-none.png"
                class="img-responsive"
                alt="Bracket marking the start of the scale, labelled None."
              />
            </div>
            <div class="vx-col xs:w-3/12">
              <img
                src="@/assets/images/experiences/physiotherapy/slider-mild.png"
                class="img-responsive"
                alt="Bracket marking the next section of the scale, labelled Mild."
              />
            </div>
            <div class="vx-col xs:w-3/12">
              <img
                src="@/assets/images/experiences/physiotherapy/slider-moderate.png"
                class="img-responsive"
                alt="Bracket marking the middle section of the scale, labelled Moderate."
              />
            </div>
            <div class="vx-col xs:w-4/12">
              <img
                src="@/assets/images/experiences/physiotherapy/slider-severe.png"
                class="img-responsive"
                alt="Bracket marking the upper section of the scale, labelled Severe."
              />
            </div>
          </div>
        </div>
        <div class="vx-col xs:w-2/12 text-left">
          <span>Severe</span>
        </div>
      </div>
    </div>

    <h4 class="mb-5">Visual Analog Scale (VAS)</h4>
    <p class="mb-5">
      Move the slider along the bar to the appropriate place to show your level of pain between "No
      pain" and "Worst possible pain"
    </p>
    <div class="vx-row mb-5">
      <div class="vx-row m-auto lg:w-2/3 xs:w-full">
        <div class="vx-col xs:w-2/12 text-right" style="align-self: center">
          <span>No pain</span>
        </div>
        <div
          class="vx-col xs:w-8/12"
          style="
            align-self: center;
            border-left: 3px rgba(var(--vs-primary), 1) solid;
            border-right: 3px rgba(var(--vs-primary), 1) solid;
          "
        >
          <accessible-slider
            v-model="data.question5"
            :max="100"
            label="Visual analogue pain scale, 0 to 100"
            :disabled="!isEditable"
            min-text="No pain"
            max-text="Worst possible pain"
          />
        </div>
        <div class="vx-col xs:w-2/12 text-left" style="align-self: center">
          <span>Worst possible pain</span>
        </div>
      </div>
    </div>
    <!--    <centered-image-wrapper :image="require('@/assets/images/experiences/physiotherapy/no_pain_worst_pain.png')" classes="3/3"/>-->

    <h4 class="mb-5">Comparative Pain Scale</h4>
    <p class="mb-5">
      Look at the Comparative Pain Scale Chart below. Choose the number that best corresponds to how
      much pain you are feeling and record in the space below.
    </p>
    <centered-image-wrapper
      :image="require('@/assets/images/experiences/physiotherapy/comparative_pain_scale.png')"
      alt="Comparative pain scale chart, 0 to 10, with a face for each number. 0 Pain free: No pain, feeling perfectly normal. 1 Very mild, 2 Discomforting, 3 Tolerable: Minor pain - nagging, annoying, but doesn't interfere with most daily living activities; patient able to adapt to pain psychologically and with medication or devices such as cushions. 4 Distressing, 5 Very distressing, 6 Intense: Moderate pain - interferes significantly with daily living activities; requires lifestyle changes but patient remains independent; patient unable to adapt pain. 7 Very intense, 8 Utterly horrible, 9 Excruciating unbearable, 10 Unimaginable unspeakable: Severe pain - disabling; unable to perform daily living activities; unable to engage in normal activities; patient is disabled and unable to function independently."
      classes="3/3"
    />
    <div class="overflow-hidden mb-5">
      <h5 class="float-left py-2 pr-2">Comparative Pain Scale Result:</h5>
      <text-block
        v-model="data.question6"
        :editable="isEditable"
        label="Comparative Pain Scale Result"
        style="width: 350px"
      />
    </div>

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import AccessibleSlider from '@/components/UI/AccessibleSlider';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  components: { AccessibleSlider },
  data: function () {
    return {
      videos: {
        1: {
          video:
            $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
            '/content/physiotherapy/pain_scales_video.mp4',
          image: require('@/assets/images/experiences/physiotherapy/pain_scale_video_trigger.jpg'),
        },
      },
    };
  },
  methods: {
    ...methods,
  },
  computed: {
    ...computed,
  },
  inject: inject,
};
</script>

<style>
.hide-slider-text .vs-slider--circle-text {
  display: none !important;
}
</style>
